import React, {FunctionComponent} from "react";
import {Grid, Typography} from "@mui/material";
import {AProgressTotalizer, Skeleton} from "@atiautomacao/ati-ui-library";
import Battery3BarOutlinedIcon from '@mui/icons-material/Battery3BarOutlined';
import EnergySavingsLeafOutlinedIcon from '@mui/icons-material/EnergySavingsLeafOutlined';
import ImportExportOutlinedIcon from '@mui/icons-material/ImportExportOutlined';
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined';
import {BessTotalizers as BessTotalizersData, FindBessTotalizersReactQuery} from "./BatteryReactQuery";
import {computePowerScalePercent} from "./BessTotalizersUtils";

const NO_DATA = "-";
const CARD_SX = {minHeight: 72};

const formatNumber = (value: number | null | undefined, decimals: number): string => {
    if (value === null || value === undefined) {
        return NO_DATA;
    }
    return value.toFixed(decimals);
};

const formatSignedPower = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
        return NO_DATA;
    }
    const sign = value > 0 ? "+" : "";
    return `${sign}${value.toFixed(1)}`;
};

const unitOf = (value: number | null | undefined, unity: string | null | undefined): string | undefined => {
    if (value === null || value === undefined) {
        return undefined;
    }
    return unity ?? undefined;
};

const ActivePowerTile: FunctionComponent<{ data: BessTotalizersData }> = ({data}) => {
    const scalePercent = computePowerScalePercent(data.activePower, data.powerScaleMin, data.powerScaleMax);

    return (
        <AProgressTotalizer
            label="Potência ativa"
            value={formatSignedPower(data.activePower)}
            unit={unitOf(data.activePower, data.activePowerUnity ?? "kW")}
            icon={EnergySavingsLeafOutlinedIcon}
            progressVariant="bipolar"
            progressValue={scalePercent}
            progressWidth="50%"
            showPercentageSign
            sx={CARD_SX}
        />
    );
};

const LoadingSkeleton: FunctionComponent = () => (
    <Grid container spacing={2} data-testid="bess-totalizers-loading">
        {Array.from({length: 4}).map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index.valueOf()}>
                <Skeleton animation="wave" height={72} variant="rounded" width="100%"/>
            </Grid>
        ))}
    </Grid>
);

const BessTotalizers: FunctionComponent<{ powerStationId?: number }> = ({powerStationId}) => {
    const query = FindBessTotalizersReactQuery(powerStationId);
    const data: BessTotalizersData | undefined = query.data?.data;

    if (query.isLoading) {
        return <LoadingSkeleton/>;
    }

    if (query.isError || !data) {
        return (
            <Typography variant="body2" sx={{color: "text.secondary", py: 1}}>
                Não foi possível carregar os totalizadores do BESS.
            </Typography>
        );
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
                <AProgressTotalizer
                    label="Normalizados"
                    value={`${data.normalizedCount}/${data.totalCount}`}
                    icon={Battery3BarOutlinedIcon}
                    sx={CARD_SX}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <ActivePowerTile data={data}/>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <AProgressTotalizer
                    label="Energia exportada"
                    value={formatNumber(data.exportedEnergy, 3)}
                    unit={unitOf(data.exportedEnergy, data.exportedEnergyUnity)}
                    icon={ImportExportOutlinedIcon}
                    sx={CARD_SX}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <AProgressTotalizer
                    label="Energia importada"
                    value={formatNumber(data.importedEnergy, 3)}
                    unit={unitOf(data.importedEnergy, data.importedEnergyUnity)}
                    icon={SystemUpdateAltOutlinedIcon}
                    sx={CARD_SX}
                />
            </Grid>
        </Grid>
    );
};

export default BessTotalizers;
